import { Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import useLanguageSkills from "../../../../hooks/useLanguageSkills";
import { Talent, TalentCertificate } from "../../../talents/talentAPI";
import {
  DRIVING_LICENSES_CERT_TYPES,
  CUSTOMER_HIDDEN_CERT_TYPES,
} from "../../constants";
import TalentSummaryCard from "../TalentSummaryCard";
import TalentSkillSetSection from "./TalentSkillSetSection";
import { endOfDay, isBefore } from "date-fns";

type Props = {
  talent: Talent;
};

const sortAlphabetically = (list: string[]) =>
  list.sort((a, b) => a.localeCompare(b));

const TalentSkillSet = ({ talent }: Props) => {
  const { t } = useTranslation("talentProfile");

  const {
    workTitle: workTitleTranslations,
    certificate: certificateTranslations,
    training: trainingTranslations,
    skill: skillTranslations,
  } = useAppSelector((state) => state.localized);

  const { getLanguageSkillsNameByCode } = useLanguageSkills();

  const languages = useMemo(() => {
    const list =
      talent?.languages?.values.map((language) => ({
        id: language.languageCode,
        name: getLanguageSkillsNameByCode(language.languageCode),
        level: language.skillLevel,
      })) || [];

    const sortedLanguages = sortAlphabetically(
      list.map((language) => language.name),
    );

    const formattedLanguages = sortedLanguages.map((name) => {
      const language = list.find((lang) => lang.name === name);
      return (
        <Typography component="span" key={language?.id}>
          {language?.name}{" "}
          <Typography component="span" color="textSecondary">
            ({t(`languageSkill:${language?.level}`)})
          </Typography>
        </Typography>
      );
    });

    return formattedLanguages;
  }, [talent, getLanguageSkillsNameByCode, t]);

  const workTitles = useMemo(() => {
    const list =
      talent?.workTitles?.values.map(
        (workTitle) => workTitleTranslations[workTitle.id],
      ) || [];
    return sortAlphabetically(list);
  }, [talent, workTitleTranslations]);

  const approvedValidTalentCertificates = (
    talent: Talent,
  ): TalentCertificate[] => {
    return (
      talent?.certificates?.filter((certificate) => {
        const okByDocument = certificate.requiresDocument
          ? certificate.approvedAt && !certificate.rejectedAt
          : true;

        if (certificate.validTo) {
          const validTo = endOfDay(certificate.validTo);
          const okByExpiry =
            certificate.validTo == null || isBefore(new Date(), validTo);
          return okByDocument && okByExpiry;
        } else {
          return okByDocument;
        }
      }) || []
    );
  };

  const certificates = useMemo(() => {
    const list =
      approvedValidTalentCertificates(talent)
        ?.filter(
          // YLPE-2510 filter out driving licenses and hidden certificates
          (certificate) =>
            !DRIVING_LICENSES_CERT_TYPES.includes(certificate.type) &&
            !CUSTOMER_HIDDEN_CERT_TYPES.includes(certificate.type),
        )
        .map((certificate) => certificateTranslations[certificate.type]) || [];
    return sortAlphabetically(list);
  }, [talent, certificateTranslations]);

  const drivingLicenses = useMemo(() => {
    const list =
      approvedValidTalentCertificates(talent)
        ?.filter((certificate) =>
          DRIVING_LICENSES_CERT_TYPES.includes(certificate.type),
        )
        .map((certificate) => certificateTranslations[certificate.type]) || [];
    return sortAlphabetically(list);
  }, [talent, certificateTranslations]);

  const trainings = useMemo(() => {
    const list =
      talent?.trainings
        ?.filter((training) => training.completed)
        .filter((training) => {
          if (!training.validTo) return true;
          return isBefore(new Date(), endOfDay(training.validTo));
        })
        .map((training) => trainingTranslations[training.type]) || [];
    return sortAlphabetically(list);
  }, [talent, trainingTranslations]);

  const skills = useMemo(() => {
    const list =
      talent?.skills?.values.map((skill) => skillTranslations[skill.id]) || [];
    return sortAlphabetically(list);
  }, [talent, skillTranslations]);

  const vehicleAvailabilityNotSet =
    talent?.vehicleAvailable === null || talent?.vehicleAvailable === undefined;

  return (
    <TalentSummaryCard containerSx={{ minHeight: "326px" }}>
      <TalentSkillSetSection
        title={t("profileSummary.languages")}
        list={languages}
      />
      <TalentSkillSetSection
        title={t("profileSummary.workTitles")}
        list={workTitles}
      />
      <TalentSkillSetSection
        title={t("profileSummary.certificates")}
        list={certificates}
      />
      <TalentSkillSetSection
        title={t("profileSummary.drivingLicenses")}
        list={drivingLicenses}
      />
      <TalentSkillSetSection
        title={t("profileSummary.trainings")}
        list={trainings}
      />
      <TalentSkillSetSection title={t("profileSummary.skills")} list={skills} />
      <TalentSkillSetSection
        title={t("profileSummary.vehicle")}
        list={[
          vehicleAvailabilityNotSet
            ? t("profileSummary.vehicleAvailabilityNotSet")
            : talent.vehicleAvailable.value
              ? t("profileSummary.vehicleAvailable")
              : t("profileSummary.vehicleNotAvailable"),
        ]}
      />
      <TalentSkillSetSection
        title={t("talentProfile:CV")}
        list={[
          talent?.cv
            ? t("profileSummary.talentHasCV")
            : t("profileSummary.talentHasNotCV"),
        ]}
      />
    </TalentSummaryCard>
  );
};

export default TalentSkillSet;
